/**
 * Apps — `/admin/apps`.
 *
 * The whole catalogue, always: an app this company does not have is exactly
 * what somebody opening this screen came to look at.
 *
 * Two things it refuses to be vague about. Dependencies are shown before they
 * are enforced, so an administrator can see that Sales needs Inventory rather
 * than discovering it from a refusal. And removal says plainly that the data
 * stays — because the fear that "uninstall" means "delete my ledger" is the
 * reason nobody would ever press it.
 */
import { useTranslation } from "react-i18next";
import { AlertCircle, Check, Lock } from "lucide-react";
import { Badge, Button, Card, CardContent, CardHeader, CardTitle } from "@erp/ui";

import { useSession } from "@/lib/session/use-session";
import { appPresentation, CORE, type AppCode } from "@/lib/modules";

import { APP_MANAGE_PERMISSION, APP_READ_PERMISSION, type AppRecord } from "../api";
import { useApps, useInstallApp, useUninstallApp } from "../queries";
import { refusalMessage } from "../refusal";

export interface AppsPageProps {
  onOpenApp?: (path: string) => void;
}

export function AppsPage({ onOpenApp }: AppsPageProps) {
  const { t } = useTranslation();
  const { hasPermission, hasModule } = useSession();
  const canRead = hasPermission(APP_READ_PERMISSION);
  const canManage = hasPermission(APP_MANAGE_PERMISSION);
  const catalogue = useApps(canRead);
  const install = useInstallApp();
  const uninstall = useUninstallApp();

  if (!canRead) {
    return (
      <div className="p-4">
        <p role="alert" className="text-sm text-destructive">
          {t("apps.readForbidden", "You cannot read this company's app list.")}
        </p>
      </div>
    );
  }

  const busy = install.isPending || uninstall.isPending;
  const failure = install.error ?? uninstall.error;
  const failureText = refusalMessage(t, failure);

  return (
    <div className="flex flex-col gap-4 p-4">
      <div className="space-y-1">
        <h1 className="text-xl font-semibold tracking-tight">{t("apps.title", "Apps")}</h1>
        <p className="text-sm text-muted-foreground">
          {t("apps.subtitle", "Install what this company works with.")}
        </p>
      </div>

      <p className="text-sm text-muted-foreground">
        {t("apps.dataKept", "Removing an app keeps its data.")}
      </p>

      {failureText === "" ? null : (
        <p role="alert" className="flex items-start gap-2 text-sm text-destructive">
          <AlertCircle className="mt-0.5 size-4 shrink-0" aria-hidden="true" />
          <span>{failureText}</span>
        </p>
      )}

      <div className="grid gap-3 sm:grid-cols-2 xl:grid-cols-3">
        {(catalogue.data ?? []).map((app) => (
          <AppCard
            key={app.code}
            app={app}
            installed={hasModule(app.code)}
            canManage={canManage}
            busy={busy}
            hasModule={hasModule}
            onOpen={onOpenApp}
            onInstall={() =>
              install.mutate({ code: app.code, expectedVersion: app.version || undefined })
            }
            onUninstall={() =>
              uninstall.mutate({ code: app.code, expectedVersion: app.version || undefined })
            }
          />
        ))}
      </div>
    </div>
  );
}

function AppCard({
  app,
  installed,
  canManage,
  busy,
  hasModule,
  onOpen,
  onInstall,
  onUninstall,
}: {
  app: AppRecord;
  installed: boolean;
  canManage: boolean;
  busy: boolean;
  hasModule: (code: AppCode) => boolean;
  onOpen?: (path: string) => void;
  onInstall: () => void;
  onUninstall: () => void;
}) {
  const { t } = useTranslation();
  const presentation = appPresentation(app.code);
  const Icon = presentation?.icon;
  const name = t(`apps.${app.code}.name`, app.name);
  // Shown before it is enforced: seeing that Sales needs Inventory beats
  // learning it from a refusal after pressing Install.
  const missing = app.depends_on.filter((dependency) => !hasModule(dependency));
  const firstMissing = missing[0] ?? null;

  return (
    <Card className="flex flex-col">
      <CardHeader className="pb-3">
        <CardTitle>
          <div className="flex items-start gap-3">
            {Icon === undefined ? null : (
              <span className="flex size-9 shrink-0 items-center justify-center rounded-lg bg-muted">
                <Icon className="size-4" aria-hidden="true" />
              </span>
            )}
            <div className="min-w-0 flex-1 space-y-1">
              <h2 className="text-base font-semibold">{name}</h2>
              {app.removable ? (
                <Badge variant={installed ? "success" : "outline"}>
                  {installed
                    ? t("apps.installed", "Installed")
                    : t("apps.notInstalled", "Not installed")}
                </Badge>
              ) : (
                <Badge variant="secondary">{t("apps.alwaysOn", "Always on")}</Badge>
              )}
            </div>
          </div>
        </CardTitle>
      </CardHeader>
      <CardContent className="flex flex-1 flex-col gap-3">
        <p className="text-sm text-muted-foreground">{t(`apps.${app.code}.tagline`, "")}</p>

        {app.depends_on.length === 0 ? null : (
          <p className="text-xs">
            <span className="text-muted-foreground">{t("apps.needs", "Needs")}: </span>
            {app.depends_on.map((dependency, index) => (
              <span key={dependency} className={hasModule(dependency) ? "" : "text-destructive"}>
                {index === 0 ? "" : " · "}
                {t(`apps.${dependency}.name`, dependency)}
              </span>
            ))}
          </p>
        )}

        <div className="mt-auto flex flex-wrap gap-2 pt-1">
          {installed && onOpen !== undefined && presentation !== undefined ? (
            <Button
              type="button"
              variant="outline"
              size="sm"
              onClick={() => onOpen(presentation.paths[0] ?? "/")}
            >
              {t("apps.open", "Open")}
            </Button>
          ) : null}
          {!canManage ? (
            app.code === CORE ? null : (
              <span className="flex items-center gap-1.5 text-xs text-muted-foreground">
                <Lock className="size-3.5" aria-hidden="true" />
                {t("apps.manageForbidden", "You cannot change which apps this company has.")}
              </span>
            )
          ) : !app.removable ? null : installed ? (
            <Button type="button" variant="ghost" size="sm" disabled={busy} onClick={onUninstall}>
              {t("apps.uninstall", "Remove")}
            </Button>
          ) : (
            <Button
              type="button"
              size="sm"
              disabled={busy || missing.length > 0}
              onClick={onInstall}
              title={
                firstMissing === null
                  ? undefined
                  : t("apps.installFirst", "Install {{name}} first.", {
                      name: t(`apps.${firstMissing}.name`, firstMissing),
                    })
              }
            >
              <Check className="size-3.5" aria-hidden="true" />
              {t("apps.install", "Install")}
            </Button>
          )}
        </div>
      </CardContent>
    </Card>
  );
}
